import type { ReactElement } from "react";
import { useMemo } from "react";

import {
    LocationAssetType,
    type LocationChipItem,
} from "app/pages/sales-intelligence/common-components/dropdown/LocationDropdown/types";
import type { ChipItem } from "app/pages/sales-intelligence/common-components/filters-panel";
import { LimitedChipItems } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";
import { MAX_CHIPS_GROUP_AMOUNT } from "app/pages/sales-intelligence/common-components/filters-panel/constants";
import { assembleEmployeeLocationFilterDTO } from "app/pages/sales-intelligence/domain/contacts/utils/filter-dto-adapters";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";
import { getSelectedCodes } from "app/pages/sales-intelligence/utils/location";
import { buildLocationChips, getCodesAfterLocationChipRemoval } from "app/pages/sales-intelligence/utils/locationChips";

import type FindEmployeeLocationFilter from "../../../models/EmployeeLocationFilter/EmployeeLocationFilter";

const EmployeeLocationFilterResult = ({
    filter,
    value,
    isExpanded,
    isActionEnabled = true,
    onGetLifecycleStatus,
    onFilterUpdate,
    onFilterValueRemove,
}: IFilterViewBaseProps<FindEmployeeLocationFilter>): ReactElement | null => {
    const { regions, countries } = filter.options;

    const onItemDelete = (id: string, item: ChipItem) => {
        const locationItem = item as LocationChipItem;
        if (!value) {
            return;
        }

        if (locationItem.type !== LocationAssetType.Region && locationItem.type !== LocationAssetType.Country) {
            return;
        }

        const newCodes = getCodesAfterLocationChipRemoval(locationItem, id, getSelectedCodes(value, regions));
        const newValue = assembleEmployeeLocationFilterDTO(
            value.inclusion ?? inclusionOptionMap.includeOnly,
            newCodes,
            [],
        );

        onFilterValueRemove?.(newValue);
        onFilterUpdate(newValue);
    };

    const chips: ChipItem[] = useMemo(
        () => buildLocationChips({ value, regions, countries, onGetLifecycleStatus }),
        [value, regions, countries, onGetLifecycleStatus],
    );

    if (!chips.length) {
        return null;
    }

    return (
        <FilterResultContainer>
            <LimitedChipItems
                items={chips}
                maxItems={MAX_CHIPS_GROUP_AMOUNT}
                isTabExpanded={isExpanded}
                hideActionButton={!isActionEnabled}
                onDelete={onItemDelete}
            />
        </FilterResultContainer>
    );
};

export default EmployeeLocationFilterResult;
